import { ReactNode, SyntheticEvent, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns/format';
import TextInput from '__components/Inputs/TextInput';
import LongText from '__components/Inputs/LongText';
import { SelectNoScrollCapture as Select } from '__components/Inputs/Select';
import {
  VARIABLE_DATA_TYPES,
  VariableDataType,
} from 'ts-components/inputs/ValueInput/types';
import DecimalNumberTextInput from '__components/Inputs/TextInput/presets/DecimalNumber';
import PhoneNumberTextInput from '__components/Inputs/TextInput/presets/PhoneNumber';
import DateInput from '__components/Inputs/DateInput';
import DateTimeInput from '__components/Inputs/DateTimeInput';
import { StyledDateWrapper } from './styles';
import SelectTeamMember from '__components/Fields/FieldInput/SelectTeamMember';
import { RecordSelect } from './RecordSelect';
import { GenericOption } from 'pages/SmartConnectors/types';
import { isEmailValid } from 'utility/validate';

type ValueInputPropsCommon = {
  dataType: null;
  error?: string[];
  setError: (prop: 'value', error: string[]) => void;
  validate?: any;
  inputFormat?: string | null;
  allowEmpty?: boolean;
  label?: string;
  disabled?: boolean;
  hideLabel?: boolean;
  margin?: number | boolean | 'dense' | 'normal';
  menuLeftButton?: ReactNode;
  menuRightButton?: ReactNode;
  menuInline?: boolean;
  isArray?: boolean;
  placeholder?: string;
};

type ValueInputPropsEmployee = Omit<ValueInputPropsCommon, 'dataType'> & {
  dataType: Extract<VariableDataType, 'employee'>;
  value: GenericOption | null;
  onChange: (value: GenericOption, isValid?: boolean) => void;
};

type ValueInputPropsEntity = Omit<ValueInputPropsCommon, 'dataType'> & {
  dataType: Extract<VariableDataType, 'entity'>;
  customObjectId: string;
  value: GenericOption | GenericOption[] | null;
  onChange: (value: GenericOption | GenericOption[], isValid?: boolean) => void;
};

type ValueInputPropsString = Omit<ValueInputPropsCommon, 'dataType'> & {
  dataType: Extract<VariableDataType, 'string' | 'uuid'>;
  value: string;
  onChange: (value: string, isValid?: boolean) => void;
  enableMultiline?: boolean;
  initialHeight?: string;
};

type ValueInputPropsPrimitive = Omit<ValueInputPropsCommon, 'dataType'> & {
  dataType: Extract<
    VariableDataType,
    'boolean' | 'number' | 'date' | 'datetime' | 'phone_number' | 'email'
  >;
  value: string;
  onChange: (value: string, isValid?: boolean) => void;
};

type ValueInputProps =
  | ValueInputPropsCommon
  | ValueInputPropsString
  | ValueInputPropsEmployee
  | ValueInputPropsEntity
  | ValueInputPropsPrimitive;

const booleanFormatLookup: Record<string, string> = {
  true_false: 'True/False',
};

const dateFormatLookup: Record<string, string> = {
  '%Y-%m-%d': 'yyyy-MM-dd',
  '%Y-%m-%d %H:%M': 'yyyy-MM-dd HH:mm',
  '%Y-%m-%d %H:%M-%S-%X': "yyyy-MM-dd'T'HH:mm:ss.000XXX",
};

export const isUUIDValid = (uuid: string) => {
  return /^(([0-9a-f]{32})|([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}))$/i.test(
    uuid
  );
};

const StringValueInput = ({
  dataType,
  value,
  onChange,
  error,
  validate,
  setError,
  allowEmpty = false,
  label,
  disabled,
  hideLabel,
  enableMultiline = false,
  initialHeight,
  placeholder,
}: ValueInputPropsString) => {
  const { t } = useTranslation();
  const isUUID = dataType === VARIABLE_DATA_TYPES.uuid;

  const [innerValue, setInnerValue] = useState<string>(value);

  const handleChange = (value: string) => {
    setInnerValue(value);
  };

  const handleBlur = () => {
    const trimmedValue = (innerValue || '').trim();
    let isValid = true;
    if (trimmedValue.length === 0 && !allowEmpty) {
      setError('value', [t('This field may not be blank.')]);
      isValid = false;
    } else if (isUUID && trimmedValue.length && !isUUIDValid(trimmedValue)) {
      setError('value', [
        t(
          'A UUID value is expected in the format: XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX (dashes optional)'
        ),
      ]);
      isValid = false;
    }
    onChange(trimmedValue, isValid);
  };

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  const commonProps = {
    value: innerValue,
    label: hideLabel ? undefined : label || t('Value'),
    placeholder:
      placeholder ??
      (isUUID ? 'XXXXXXXX-XXXX-XXXX-XXXX-XXXXXXXXXXXX' : t('Enter Text')),
    onChange: handleChange,
    onBlur: handleBlur,
    error: error,
    validate: validate,
    disabled: disabled,
  };

  return enableMultiline ? (
    <LongText {...commonProps} initialHeight={initialHeight} />
  ) : (
    <TextInput {...commonProps} />
  );
};

const EmailValueInput = ({
  value,
  onChange,
  error,
  validate,
  setError,
  allowEmpty = false,
  label,
  disabled,
  hideLabel,
}: ValueInputPropsPrimitive) => {
  const { t } = useTranslation();

  const [innerValue, setInnerValue] = useState<string>(value);

  const handleChange = (value: string) => {
    setInnerValue(value);
  };

  const handleBlur = () => {
    const trimmedValue = (innerValue || '').trim();
    let isValid = true;
    if (trimmedValue.length === 0 && !allowEmpty) {
      setError('value', [t('This field may not be blank.')]);
      isValid = false;
    } else if (trimmedValue.length && !isEmailValid(trimmedValue)) {
      setError('value', [
        t(
          'A valid email address is expected in the format: example@domain.com'
        ),
      ]);
      isValid = false;
    }
    onChange(trimmedValue, isValid);
  };

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  return (
    <TextInput
      value={innerValue}
      label={hideLabel ? undefined : label || t('Value')}
      placeholder={t('Enter Email')}
      onChange={handleChange}
      onBlur={handleBlur}
      error={error}
      validate={validate}
      disabled={disabled}
    />
  );
};

const BooleanValueInput = ({
  value,
  onChange,
  error,
  validate,
  inputFormat,
  label,
  menuLeftButton,
  menuRightButton,
  disabled,
  hideLabel,
}: ValueInputPropsPrimitive) => {
  const { t } = useTranslation();

  const handleChange = (option: { value: string } | null) => {
    onChange(option?.value || '');
  };

  const options = useMemo(
    () =>
      inputFormat
        ? booleanFormatLookup[inputFormat]
            .split('/')
            .map((v, i) => ({ value: v, label: i === 0 ? t('Yes') : t('No') }))
        : [
            { value: 'True', label: t('Yes') },
            { value: 'False', label: t('No') },
          ],
    [t, inputFormat]
  );

  return (
    <Select
      value={value}
      label={hideLabel ? undefined : label || t('Value')}
      placeholder={t('Select Value')}
      onChange={handleChange}
      options={options}
      error={error}
      validate={validate}
      menuLeftButton={menuLeftButton}
      menuRightButton={menuRightButton}
      disabled={disabled}
    />
  );
};

const NumberValueInput = ({
  value,
  onChange,
  error,
  setError,
  validate,
  allowEmpty = false,
  label,
  hideLabel,
}: ValueInputPropsPrimitive) => {
  const { t } = useTranslation();

  const [innerValue, setInnerValue] = useState<string>(value);

  const handleBlur = () => {
    let isValid = true;
    if (!allowEmpty && isNaN(parseFloat(innerValue))) {
      setError('value', [t('This field may not be blank.')]);
      isValid = false;
    }
    onChange(innerValue, isValid);
  };

  useEffect(() => {
    setInnerValue(value);
  }, [value]);

  return (
    <DecimalNumberTextInput
      value={innerValue}
      label={hideLabel ? undefined : label || t('Value')}
      placeholder={t('0.00')}
      onChange={setInnerValue}
      onBlur={handleBlur}
      error={error}
      validate={validate}
    />
  );
};

const PhoneNumberValueInput = ({
  value,
  onChange,
  error,
  setError,
  validate,
  label,
  hideLabel,
}: ValueInputPropsPrimitive) => {
  const { t } = useTranslation();

  const handleChange = (
    value: string,
    e: SyntheticEvent,
    data: null | { errorMessage: string | null }
  ) => {
    let isValid = true;
    if (!value?.trim()) {
      setError('value', [t('This field may not be blank.')]);
      isValid = false;
    } else if (data?.errorMessage) {
      setError('value', [data.errorMessage]);
      isValid = false;
    }
    onChange(value, isValid);
  };

  return (
    <PhoneNumberTextInput
      label={hideLabel ? undefined : label || t('Value')}
      value={value}
      placeholder={t('Enter Phone Number')}
      onChange={handleChange}
      error={!!error}
      validate={validate}
      enableExtension
      captureMenuScroll={false}
    />
  );
};

const DateValueInput = ({
  dataType,
  value,
  onChange,
  error,
  validate,
  inputFormat,
  label,
  menuInline,
  hideLabel,
}: ValueInputPropsPrimitive) => {
  const { t } = useTranslation();
  const isDateTime = dataType === VARIABLE_DATA_TYPES.datetime;
  const handleChange = (value: string) => {
    onChange(
      inputFormat
        ? format(new Date(value), dateFormatLookup[inputFormat])
        : value
    );
  };

  const Component = isDateTime ? DateTimeInput : DateInput;

  return (
    <StyledDateWrapper>
      <Component
        label={hideLabel ? undefined : label || t('Value')}
        value={value}
        placeholder={'MM/DD/YYYY'}
        onChange={handleChange}
        error={!!error}
        validate={validate}
        variant="outline"
        menuInline={menuInline}
        captureMenuScroll={false}
      />
    </StyledDateWrapper>
  );
};

const EmployeeValueInput = ({
  value,
  onChange,
  error,
  validate,
  label,
  menuLeftButton,
  menuRightButton,
  disabled,
  hideLabel,
}: ValueInputPropsEmployee) => {
  const { t } = useTranslation();
  return (
    <SelectTeamMember
      label={hideLabel ? undefined : label || t('Team Member')}
      placeholder={t('Select Team Member')}
      value={value}
      onChange={onChange}
      error={!!error}
      validate={validate}
      variant="outline"
      field={{
        isDefault: true,
      }}
      menuLeftButton={menuLeftButton}
      menuRightButton={menuRightButton}
      disabled={disabled}
      captureMenuScroll={false}
    />
  );
};

const RecordValueInput = ({
  value,
  onChange,
  error,
  validate,
  label,
  customObjectId,
  menuInline,
  menuLeftButton,
  menuRightButton,
  hideLabel,
  margin,
  isArray = false,
  ...rest
}: ValueInputPropsEntity) => {
  const { t } = useTranslation();

  return (
    <RecordSelect
      customObjectId={customObjectId}
      label={
        hideLabel ? undefined : label || (isArray ? t('Records') : t('Record'))
      }
      value={value}
      onChange={onChange}
      error={!!error}
      validate={validate}
      variant="outline"
      menuInline={menuInline}
      menuLeftButton={menuLeftButton}
      menuRightButton={menuRightButton}
      margin={margin}
      isArray={isArray}
      {...rest}
    />
  );
};

export const ValueInput = (inputProps: ValueInputProps) => {
  switch (inputProps.dataType) {
    case VARIABLE_DATA_TYPES.boolean:
      return <BooleanValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.string:
    case VARIABLE_DATA_TYPES.uuid:
      return <StringValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.number:
      return <NumberValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.phone_number:
      return <PhoneNumberValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.date:
    case VARIABLE_DATA_TYPES.datetime:
      return <DateValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.employee:
      return <EmployeeValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.entity:
      return <RecordValueInput {...inputProps} />;
    case VARIABLE_DATA_TYPES.email:
      return <EmailValueInput {...inputProps} />;
    default:
      return null;
  }
};
